import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A matrix chart is a grid-based data visualization that organizes information into rows and
          columns, forming a matrix where intersections represent relationships or values. This
          format is ideal for analyzing relationships, comparisons, or patterns among multiple
          variables. Matrix charts are widely used in fields such as business analysis, project
          management, and decision-making frameworks.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Matrix charts are a versatile visualization tool for comparing multiple variables and
          displaying relationships in a compact, organized format:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Displaying Relationships Between Multiple Variables:</strong> Matrix charts are
            ideal for showcasing correlations or patterns across several dimensions, allowing users
            to quickly compare data points in a grid format.
          </li>
          <li>
            <strong>Identifying Patterns and Clusters:</strong> The chart allows for easy
            identification of trends, clusters, or outliers by visually representing data in a way
            that highlights similarities and differences across categories.
          </li>
          <li>
            <strong>Summarizing Complex Data:</strong> Matrix charts simplify complex datasets by
            breaking them down into smaller, more manageable cells, making it easier to analyze
            interactions and dependencies between multiple variables at once.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Matrix charts are effective for displaying data relationships, but they come with certain
          limitations that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Overcrowding with Large Datasets:</strong> Matrix charts can become difficult to
            interpret when there are too many categories or data points. The grid can get
            overcrowded, making it challenging to extract meaningful insights.
          </li>
          <li>
            <strong>Limited Context for Data Values:</strong> Matrix charts often rely on color or
            size to represent values, which can make it hard to understand the actual magnitude or
            context of each value without detailed labels or additional context.
          </li>
          <li>
            <strong>Difficulty in Visualizing Complex Interactions:</strong> Matrix charts are
            useful for displaying pairwise relationships, but they may not effectively show complex
            interactions or correlations across multiple dimensions, limiting their ability to
            represent intricate data structures.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection plays a critical role in Matrix charts, enabling viewers to quickly
          identify patterns and relationships within the data. Gradient color scales are commonly
          used, with varying intensities of a single hue or a spectrum to represent different value
          ranges. Diverging palettes are ideal for highlighting deviations from a central reference
          point, such as a baseline or average. Thoughtful color choices enhance readability and
          ensure that the visual representation aligns with the data’s intended message.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Matrix charts are versatile for analyzing relationships in a structured grid format, but
          they present certain challenges that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Complexity with Large Data Sets:</strong> As the matrix grows, it becomes
            increasingly difficult to interpret due to visual clutter and overlapping elements,
            limiting its usability for extensive datasets.
          </li>
          <li>
            <strong>Limited Insight into Trends:</strong> Matrix charts focus on individual
            relationships rather than overall trends or patterns, making them less effective for
            temporal or progression-based analyses.
          </li>
          <li>
            <strong>Dependency on Clear Labeling:</strong> The chart&apos;s effectiveness relies
            heavily on concise, accurate row and column labels; unclear or overly detailed labels
            can confuse the viewer and obscure insights.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
